import type { CreateRecordType } from 'components/create-dialog/recordTypes';
import posthog from 'posthog-js';
import { isPostHogEnabled } from './posthog';

/**
 * Form events. A form goes out by its key from the fixed list below, a failed field by its key in the form's code,
 * and nothing here carries what was typed in it.
 */

/** A form no create dialog holds has a key of its own: an edit form's is its create key plus `-edit`. */
export type FormKey =
    | CreateRecordType
    | 'case-edit'
    | 'contact-edit'
    | 'timecard-edit'
    | 'timecard-finish'
    | 'bd-effort-edit'
    | 'task-edit'
    | 'invoice-edit'
    | 'invoice-prepaid'
    | 'invoice-credit-note';

/**
 * The key each form is sent as. Spelled out, so that renaming a record type is an edit here rather than a silent
 * break in the events' history, and a record type without a key does not compile.
 */
const FORM_KEYS: Record<FormKey, string> = {
    case: 'case',
    contact: 'contact',
    timecard: 'timecard',
    expense: 'expense',
    contract: 'contract',
    'client-asset': 'client-asset',
    correspondence: 'correspondence',
    debriefing: 'debriefing',
    'experience-record': 'experience-record',
    task: 'task',
    'bd-effort': 'bd-effort',
    project: 'project',
    training: 'training',
    'marketing-profile': 'marketing-profile',
    employee: 'employee',
    group: 'group',
    team: 'team',
    office: 'office',
    'practice-area': 'practice-area',
    'job-role': 'job-role',
    'job-position': 'job-position',
    'case-edit': 'case-edit',
    'contact-edit': 'contact-edit',
    'timecard-edit': 'timecard-edit',
    'timecard-finish': 'timecard-finish',
    'bd-effort-edit': 'bd-effort-edit',
    'task-edit': 'task-edit',
    'invoice-edit': 'invoice-edit',
    'invoice-prepaid': 'invoice-prepaid',
    'invoice-credit-note': 'invoice-credit-note',
};

/** How a create dialog stopped being open. A `minimised` one can open again from its tab. */
export type CreateDialogOutcome = 'created' | 'kept_as_draft' | 'discarded' | 'minimised';

function capture(event: string, properties: Record<string, unknown>): void {
    if (!isPostHogEnabled) {
        return;
    }
    posthog.capture(event, properties);
}

export function captureCreateDialogOpened(form: CreateRecordType): void {
    capture('create_dialog_opened', { form: FORM_KEYS[form] });
}

export interface CreateDialogClosedEvent {
    form: CreateRecordType;
    outcome: CreateDialogOutcome;
    /** Whole seconds since the dialog last opened. */
    secondsOpen: number;
}

export function captureCreateDialogClosed({ form, outcome, secondsOpen }: CreateDialogClosedEvent): void {
    capture('create_dialog_closed', { form: FORM_KEYS[form], outcome, seconds_open: secondsOpen });
}

export interface FormRejectedEvent {
    form: FormKey;
    /** The keys of the fields that failed: a nested or list field's is its top-level one. Sent sorted. */
    invalidFields: string[];
}

/** A submit the form's validation refused, once per attempt. */
export function captureFormRejected({ form, invalidFields }: FormRejectedEvent): void {
    capture('form_rejected', { form: FORM_KEYS[form], invalid_fields: [...invalidFields].sort() });
}
